<template>
  <div class="rating-trends-map">
    <div class="dashboards-filters">
      <div class="dashboards-filters">
        <strong>{{ title }}</strong>
      </div>
      <div class="dashboards-filters-date date-simple">
        <date-picker
          v-model="params.creation_date"
          format="D MMM"
          range-separator="-"
          :editable="false"
          :shortcuts="false"
          input-class="date-simple-input"
          range
          lang="en"
          class="dark"
        ></date-picker>
      </div>

      <div class="dashboards-filters">
        <filter-multiselect
          v-model="params.country"
          :options="countriesList"
          field-name="CountryName"
          placeholder="Select country"
          required
        />
      </div>

      <div class="dashboards-filters">
        <language-picker
          :options="languagesList"
          v-model="params.sourceLanguage"
          placeholder="Source language"
        ></language-picker>
      </div>

      <div class="dashboards-filters">
        <language-picker
          :options="languagesList"
          v-model="params.targetLanguage"
          placeholder="Target language"
        ></language-picker>
      </div>

      <div class="dashboards-filters">
        <filter-multiselect
          v-model="params.industry_field"
          :options="industryFieldList"
          field-name="Name"
          placeholder="Industry sector(s)"
        />
      </div>

      <div class="dashboards-filters">
        <filter-multiselect
          v-model="params.doc_type"
          :options="textCategoryList"
          field-name="Name"
          placeholder="Document type(s)"
        />
      </div>

      <div class="dashboards-filters">
        <filter-multiselect
          v-model="params.qaModel"
          :options="qaModelsList"
          field-name="Name"
          placeholder="QA model(s)"
        />
      </div>

      <div class="dashboards-filters">
        <filter-multiselect
          v-model="params.qaMethod"
          :options="qaMethodsList"
          field-name="Name"
          placeholder="Method(s)"
        />
      </div>

    </div>

    <div class="map-chart-wrapper">
      <pre-loader :value="load" background="#ffffff" />
      <div class="map-wrapper" v-if="loaded">
        <chart-constructor :options="mapOptions" constructor-type="mapChart" />
      </div>
      <div class="line-wrapper" v-if="loaded">
        <chart-constructor :options="lineOptions" />
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import DatePicker from 'vue2-datepicker';
import moment from 'moment';

import helpers from '@/mixins/helpers';

import Api from '@/utils/api';

export default Vue.extend({
  name: 'MapMainView',
  mixins: [
    helpers.work_with_date,
  ],
  components: {
    DatePicker,
    'language-picker': () => import('@/components/selectors/language/index.vue'),
    'filter-multiselect': () => import('@/components/selectors/filterMultiple/index.vue'),
    'chart-constructor': () => import('@/components/charts/chartConstructor/index'),
    'pre-loader': () => import('@/components/preloader/index.vue'),
  },
  props: {
    type: {
      type: String,
      default: () => '',
    },
    title: {
      type: String,
      default: () => 'KPI',
    },
  },
  data: () => ({
    mapSelectedCountries: Array(),
    textCategoryList: [],
    industryFieldList: [],
    roleFieldList: [{
      ID: 'QA',
      Name: 'QA expert',
    }, {
      ID: 'PR',
      Name: 'Proofreader',
    }],
    languagesList: Array(),
    countriesList: Array(),
    qaMethodsList: Array(),
    qaModelsList: Array(),

    params: {
      user_type: {
        ID: 'PR',
        Name: 'Proofreader',
      },
      creation_date: Array(),
      industry_field: Array(),
      doc_type: Array(),
      manager: '',
      sourceLanguage: null,
      targetLanguage: null,
      country: Array(),
      qaModel: Array(),
      qaMethod: Array(),
    },

    load: true,
    loaded: false,
    graphicsData: Array(),
  }),
  watch: {
    params: {
      handler () {
        this.getReportData();
      },
      deep: true,
    },
  },
  computed: {
    backendType () {
      switch (this.type) {
      case 'language': return 'Lang';
      case 'industry': return 'IndSect';
      case 'document': return 'DocType';
      case 'qa': return 'QAModel';
      case 'evaluation': return 'QAMeth';
      default: return '';
      }
    },
    mapSeries () {
      return this.graphicsData.map((el) => ({
        value: el.WordsCount,
        code: this.countriesList.find((c) => c.ID === el.CountryID).CountryIsoCode,
        name: this.countriesList.find((c) => c.ID === el.CountryID).CountryName,
        CountryID: el.CountryID,
        color: this.mapSelectedCountries.includes(el.CountryID) ? '#a4edba' : undefined,
      }));
    },
    mapOptions () {
      const that = this;
      return {
        chart: {
          map: 'custom/world',
        },
        title: {
          text: null,
        },
        mapNavigation: {
          enabled: true,
          buttonOptions: {
            verticalAlign: 'bottom',
          },
        },
        colorAxis: {
          title: 'words',
        },
        series: [{
          data: [...this.mapSeries],
          joinBy: ['iso-a2', 'code'],
          name: 'Number of words',
          states: {
            hover: {
              color: '#a4edba47',
            },
            select: {
              color: '#a4edba',
            },
          },
          point: {
            events: {
              click: function() {
                if (that.mapSelectedCountries.includes(this.CountryID)) {
                  that.mapSelectedCountries = [...that.mapSelectedCountries.filter((el) => el !== this.CountryID)];
                } else {
                  that.mapSelectedCountries.push(this.CountryID);
                }
              },
            },
          },
        }],
        exporting: {
          buttons: {
            contextButton: {
              menuItems: ['downloadPNG', 'downloadJPEG', 'downloadPDF', 'downloadSVG'],
            },
          },
        },
      };
    },
    lineSeries () {
      return this.graphicsData.filter((el) => this.mapSelectedCountries.includes(el.CountryID)).reduce((acc, el) => {
        const out = el.Data.map((item) => {
          const name = (() => {
            switch (this.type) {
            case 'language':
              const sourceLang = this.languagesList.find((lang) => lang.ID === item.SourceLanguageID);
              const targetLang = this.languagesList.find((lang) => lang.ID === item.TargetLanguageID);
              const sourceLangName = [sourceLang.LanguageISOCode, sourceLang.CountryIsoCode].filter((lng) => !!lng).join('-');
              const targetLangName = [targetLang.LanguageISOCode, targetLang.CountryIsoCode].filter((lng) => !!lng).join('-');
              return `${this.countriesList.find((country) => country.ID === el.CountryID).CountryName} — ${sourceLangName} > ${targetLangName}`;
            case 'industry': return `${this.countriesList.find((country) => country.ID === el.CountryID).CountryName} — ${this.industryFieldList.find((ind) => ind.ID === item.Id).Name}`;
            case 'document': return `${this.countriesList.find((country) => country.ID === el.CountryID).CountryName} — ${this.textCategoryList.find((doc) => doc.ID === item.Id).Name}`;
            case 'qa': return `${this.countriesList.find((country) => country.ID === el.CountryID).CountryName} — ${this.qaModelsList.find((qa) => qa.ID === item.Id).Name}`;
            case 'evaluation': return `${this.countriesList.find((country) => country.ID === el.CountryID).CountryName} — ${this.qaMethodsList.find((evaluate) => evaluate.ID === item.Id).Name}`;
            default: return '';
            }
          })();
          return {
            name,
            data: item.TrendsData.map((gr) => [gr.Date * 1000, gr.ReportsCount]).sort((a, b) => a[0] - b[0]),
          };
        });
        return [...acc, ...out];
      }, Array());
    },
    lineOptions () {
      return {
        chart: {
          zoomType: 'x',
        },
        title: {
          text: this.title,
        },
        xAxis: {
          type: 'datetime',
        },
        yAxis: {
          title: {
            text: 'Number of reports',
          },
        },
        legend: {
          enabled: true,
        },
        series: [...this.lineSeries],
        exporting: {
          buttons: {
            contextButton: {
              menuItems: ['downloadPNG', 'downloadJPEG', 'downloadPDF', 'downloadSVG'],
            },
          },
        },
      };
    },
  },
  methods: {
    getFilters () {
      return Promise.all([
        this.$store.dispatch('GET_LANGUAGES').then((data) => {
          this.languagesList = [...data];
          this.languagesList.unshift({
            ID: '',
            LanguageName: ' All',
            CountryISOCode: 'world',
          });
        }),
        this.$store.dispatch('GET_INDUSTRY').then((data) => {
          this.industryFieldList = [...data];
        }),
        this.$store.dispatch('GET_COUNTRIES').then((data) => {
          this.countriesList = [...data];
        }),
        this.$store.dispatch('GET_DOCUMENT_TYPES').then((data) => {
          this.textCategoryList = [...data];
        }),
        this.$store.dispatch('GET_QA_METHODS').then((data) => {
          this.qaMethodsList = [...data];
        }),
        Api.getEvaluationMethods().then((resp) => {
          if (resp.data.Status) {
            this.qaModelsList = [...resp.data.Data];
          }
        }),
      ]).then(() => this.$nextTick());
    },
    getBackendParams(params) {
      const output = {
        CountryIds: params.country.map((el) => el.ID),
        SelectedDates: params.creation_date.length > 0
          ? [ ...params.creation_date.map((e) => moment.utc(e).unix()) ] : null,
        SourceLanguageId: params.sourceLanguage ? params.sourceLanguage.ID : null,
        TargetLanguageId: params.targetLanguage ? params.targetLanguage.ID : null,
        IndustrySectorIds: params.industry_field.map((e) => e.ID),
        DocumentTypeIds: params.doc_type.map((e) => e.ID),
        QaModelIds: params.qaModel.map((e) => e.ID),
        EvMethodIds: params.qaMethod.map((e) => e.ID),
        ModelType: `${this.backendType}Trends`,
      };
      Object.keys(output).forEach((key) => {
        if (!output[key]) {
          delete output[key];
        }
      });
      return output;
    },
    getReportData () {
      if (this.params.country.length > 0) {
        this.load = true;
        this.mapSelectedCountries = Array();
        Api.dashboard.getCountriesTrends(this.getBackendParams(this.params)).then((data) => {
          const key = Object.keys(data)[0];
          this.graphicsData = data[key];
        }).finally(() => {
          this.load = false;
        });
      } else {
        this.load = true;
        setTimeout(() => {
          this.graphicsData = Array();
          this.load = false;
        }, 600);
      }
    },
  },
  created () {
    this.getFilters().then(() => {
      this.load = false;
      this.loaded = true;
    });
  },
  beforeDestroy() {
    this.loaded = false;
    this.graphicsData = Array();
  },
});
</script>

<style lang="scss">
.rating-trends-map {
    position: relative;
    margin-bottom: 30px;

    .highcharts{
        &-container {
            box-shadow: $shadow;
        }
    }

    .dashboards-filters {
        margin-bottom: 0;
        flex-flow: row wrap;
        height: auto;

        &-date {
          margin-left: 40px;
        }
    }

    &-tooltip {
        position: absolute;
        bottom: 10px;
        left: 20px;
        width: auto;
        padding: 1px;
        height: auto;
        background: #fff;
        border: 1px solid #313233;
        z-index: 1000;

        .close {
            position: absolute;
            top: 5px;
            right: 5px;
            border: 0;
            padding: 5px;
            display: block;
            font-size: 20px;
            background: #fff;
            z-index: 111111;

            i {
                color: #313233;
                transition: color 0.3s;

                &:hover {
                    color: #cccccc;
                }
            }
        }
    }
}
.rating-map-control {
    margin-top: 20px;
}
.wrapper-map {
    position: relative;
    height: 100%;
}
  .map-chart-wrapper {
    display: flex;
    flex-flow: row nowrap;
    width: 100%;
    align-items: stretch;
    min-height: 400px;
    position: relative;

    .map-wrapper {
      flex-basis: 65%;
      background: #fff;
    }

    .line-wrapper {
      flex-basis: 35%;
      background: #fff;
    }
  }
</style>
